import * as React from 'react'
import { FormikHelpers } from 'formik'
import styled from 'styled-components'

import { Button, get, Space } from '@ally/metronome-ui'
import { Email, Form, Forms } from '@ally/forms'
import { AsyncStatus } from '@ally/transmitigator'

import { ForgotUsernameModal } from '../../../Modals'
import {
  CIAMErrorBanner,
  CustomName,
  InputWrapper,
  StyledTextBody,
} from '../../Shared'
import { useAppState } from '../../../AppStateProvider'
import { useTransmit } from '../../../TransmitContext'
import { useHostServices } from '../../../HostServices'
import { FeatureFlag } from '../../../Utils'

const StyledButton = styled(Button)`
  font-size: ${get.fontSizes('sm')};
`

const FORM_ID = 'ally-login-forgot-username-personal-account-form'

interface PersonalAccountFormInputs {
  email: string
  lastName: string
}

interface PersonalAccountFormProps {
  error?: string
  status: AsyncStatus | 'Blocked' | 'Error'
  toggle: () => void
}

export const PersonalAccountForm: React.FC<PersonalAccountFormProps> = ({
  error,
  status,
  toggle,
}) => {
  const { isWebWrapped } = useAppState()
  const { featureFlags } = useHostServices()
  const { actions } = useTransmit()
  const initialValues = {
    email: '',
    lastName: '',
  }

  const onSubmit = (
    { email, lastName }: PersonalAccountFormInputs,
    { setSubmitting }: FormikHelpers<PersonalAccountFormInputs>,
  ): void => {
    actions.forgotUsername({
      email,
      lastName,
      isAutoBusinessUser: false,
    })

    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  const isAutoEnabled: boolean = featureFlags.variation(
    FeatureFlag.AutoNGA,
    false,
  )

  return (
    <>
      <Space mb="md">
        <StyledTextBody size="sm" tag="p" data-testid="header-body">
          Tell us your last name and the email address associated with your
          account. If you don&apos;t know your email address,{' '}
          <ForgotUsernameModal
            buttonText="we're here to help."
            withAllyTM={
              isWebWrapped
                ? 'ally mobile app:Web View:Forgot Username:Enter Social Security Number|We Are Here To Help'
                : 'wereHereToHelp'
            }
            variation={featureFlags.variation}
          />
        </StyledTextBody>
      </Space>

      {isAutoEnabled && (
        <Space mb="md">
          <StyledButton
            withAllyTM="haveAutoBusinessAccount"
            onClick={toggle}
            text="Have an auto business account?"
            variant="link"
          />
        </Space>
      )}

      <CIAMErrorBanner mb="md" error={error} />

      <Forms formId={FORM_ID} initialValues={initialValues} onSubmit={onSubmit}>
        <Form id={FORM_ID}>
          <InputWrapper mb="md">
            <CustomName
              variant="Last Name"
              name="lastName"
              data-allytmfn="lastName"
              required
              data-testid="forgot-username-last-name"
            />
          </InputWrapper>

          <InputWrapper mb="md">
            <Email
              data-testid="forgot-username-email"
              name="email"
              data-allytmfn="email"
              required
            />
          </InputWrapper>

          <Button
            withAllyTM="continue"
            type="submit"
            variant="primary"
            text="Continue"
            loading={status === 'Loading'}
          />
        </Form>
      </Forms>
    </>
  )
}
